import React, { useState, useEffect, useRef, useCallback } from 'react';
import apiClient from '../../api/apiClient';

const PRIMARY = '#6366f1';
const PRIMARY_DARK = '#4f46e5';

function ChatBubbleIcon({ size = 24 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
    </svg>
  );
}
function XIcon({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}
function SendIcon({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" />
    </svg>
  );
}
function SparkleIcon({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3v4M12 17v4M3 12h4M17 12h4M5.6 5.6l2.8 2.8M15.6 15.6l2.8 2.8M5.6 18.4l2.8-2.8M15.6 8.4l2.8-2.8" />
    </svg>
  );
}

export default function ChatDrawer() {
  const [open, setOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const scrollRef = useRef(null);

  const loadHistory = useCallback(async () => {
    try {
      const res = await apiClient.get('/chat/history');
      setMessages(res.data.messages || []);
    } catch {
      // silent - chat just opens empty if history can't load
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (open && !loaded) loadHistory();
  }, [open, loaded, loadHistory]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  const send = async (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || sending) return;
    setInput('');
    setError(null);
    setMessages(m => [...m, { role: 'user', content: text, id: `local-${Date.now()}` }]);
    setSending(true);
    try {
      const res = await apiClient.post('/chat/message', { message: text });
      setMessages(m => [...m, { role: 'assistant', content: res.data.reply, tool_calls: res.data.toolCalls, id: `local-${Date.now()}-a` }]);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setSending(false);
    }
  };

  const newChat = async () => {
    if (!window.confirm('Start a new chat? This clears the current conversation.')) return;
    try { await apiClient.delete('/chat/history'); } catch {}
    setMessages([]);
  };

  return (
    <>
      <button onClick={() => setOpen(o => !o)} title="AI Assistant" style={st.fab}>
        {open ? <XIcon size={22} /> : <ChatBubbleIcon size={22} />}
      </button>

      {open && (
        <div style={st.panel}>
          <div style={st.header}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <SparkleIcon size={16} />
              <span style={{ fontWeight: '700', fontSize: '0.9rem' }}>AI Assistant</span>
            </div>
            <button onClick={newChat} style={st.newChatBtn}>New chat</button>
          </div>

          <div ref={scrollRef} style={st.messages}>
            {messages.length === 0 && !sending && (
              <div style={st.emptyState}>
                Ask me about revenue, leads, memberships, CSR calls, dispatch, Win the Day, or the L10 Scorecard/Rocks/Issues/To-dos - I'll pull the real numbers.
              </div>
            )}
            {messages.map((m) => (
              <div key={m.id} style={{ ...st.bubbleRow, justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start' }}>
                <div style={m.role === 'user' ? st.bubbleUser : st.bubbleAssistant}>
                  {m.content}
                  {m.tool_calls && m.tool_calls.length > 0 && (
                    <div style={st.toolChips}>
                      {m.tool_calls.map((t, i) => <span key={i} style={st.toolChip}>{t.name.replace(/^get_/, '').replace(/_/g, ' ')}</span>)}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {sending && (
              <div style={{ ...st.bubbleRow, justifyContent: 'flex-start' }}>
                <div style={st.bubbleAssistant}>Thinking...</div>
              </div>
            )}
            {error && <div style={st.error}>{error}</div>}
          </div>

          <form onSubmit={send} style={st.inputRow}>
            <input value={input} onChange={e => setInput(e.target.value)} placeholder="Ask a question..." style={st.input} disabled={sending} />
            <button type="submit" disabled={sending || !input.trim()} style={st.sendBtn}><SendIcon size={15} /></button>
          </form>
        </div>
      )}
    </>
  );
}

const st = {
  fab: {
    position: 'fixed', bottom: '24px', right: '24px', width: '52px', height: '52px', borderRadius: '50%',
    background: `linear-gradient(135deg, ${PRIMARY}, ${PRIMARY_DARK})`, color: '#fff', border: 'none',
    boxShadow: '0 4px 16px rgba(79,70,229,0.4)', cursor: 'pointer', zIndex: 9998,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  panel: {
    position: 'fixed', bottom: '86px', right: '24px', width: '380px', maxWidth: 'calc(100vw - 48px)',
    height: '560px', maxHeight: 'calc(100vh - 130px)', background: '#fff', borderRadius: '1rem',
    boxShadow: '0 8px 30px rgba(15,23,42,0.25)', border: '1px solid #e5e7eb', zIndex: 9998,
    display: 'flex', flexDirection: 'column', overflow: 'hidden',
  },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.85rem 1rem',
    background: `linear-gradient(135deg, ${PRIMARY}, ${PRIMARY_DARK})`, color: '#fff',
  },
  newChatBtn: {
    background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.25)', color: '#fff',
    borderRadius: '0.4rem', padding: '0.25rem 0.6rem', fontSize: '0.7rem', fontWeight: '600', cursor: 'pointer',
  },
  messages: { flex: 1, overflowY: 'auto', padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.6rem' },
  emptyState: { fontSize: '0.78rem', color: '#64748b', lineHeight: 1.5, padding: '0.5rem' },
  bubbleRow: { display: 'flex' },
  bubbleUser: {
    background: PRIMARY, color: '#fff', borderRadius: '0.9rem 0.9rem 0.2rem 0.9rem',
    padding: '0.5rem 0.75rem', fontSize: '0.8rem', maxWidth: '80%', lineHeight: 1.45, whiteSpace: 'pre-wrap',
  },
  bubbleAssistant: {
    background: '#f1f5f9', color: '#1e293b', borderRadius: '0.9rem 0.9rem 0.9rem 0.2rem',
    padding: '0.5rem 0.75rem', fontSize: '0.8rem', maxWidth: '85%', lineHeight: 1.45, whiteSpace: 'pre-wrap',
  },
  toolChips: { display: 'flex', flexWrap: 'wrap', gap: '0.3rem', marginTop: '0.4rem' },
  toolChip: {
    fontSize: '0.6rem', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.03em',
    background: '#e0e7ff', color: PRIMARY_DARK, borderRadius: '999px', padding: '0.15rem 0.5rem',
  },
  error: { fontSize: '0.75rem', color: '#dc2626', padding: '0.4rem 0.5rem' },
  inputRow: { display: 'flex', gap: '0.5rem', padding: '0.75rem', borderTop: '1px solid #e5e7eb' },
  input: {
    flex: 1, padding: '0.5rem 0.75rem', border: '1.5px solid #e2e8f0', borderRadius: '0.6rem',
    fontSize: '0.8rem', outline: 'none',
  },
  sendBtn: {
    width: '38px', height: '38px', borderRadius: '0.6rem', background: PRIMARY, color: '#fff',
    border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
  },
};
